"use client";

import Card from "@packages/components/ui/Card";
import Heading from "@packages/components/ui/Heading";
import LinkButton from "@packages/components/ui/LinkButton";
import { LIST } from "@packages/design-system/constants";
import { DebugStateInfo } from "@packages/lib/auth/utils";
import React from "react";

export type AuthErrorDisplayProps = {
  errorType: string;
  errorCode?: string;
  errorMessage?: string;
  diagnosticInfo?: Record<string, unknown>;
  debugInfo?: DebugStateInfo;
  retryUrl?: string;
  retryLabel?: string;
};

export function AuthErrorDisplay({
  errorType,
  errorCode = "UNKNOWN",
  errorMessage,
  diagnosticInfo = {},
  debugInfo = {},
  retryUrl = "/auth/logout",
  retryLabel = "Log Out and Retry",
}: AuthErrorDisplayProps) {
  return (
    <div className="bg-destructive-foreground h-full w-full p-8">
      <Heading
        level={2}
        headingSize={3}
        heading={errorType}
        subheading={`Error code: ${errorCode}`}
        description={
          errorMessage ||
          "We've encountered an issue with the authentication process."
        }
        className="text-destructive-heading"
      />
      <ul className={`${LIST} text-destructive-heading`}>
        <li>Browser cookie or privacy settings blocking authentication</li>
        <li>Cross-site tracking prevention</li>
        <li>Browser extensions or content blockers</li>
        <li>Temporary authentication service disruption</li>
      </ul>
      <div className="text-destructive-heading mt-6 space-y-4">
        <p className="font-medium">Please try:</p>
        <ul className={`${LIST} text-destructive-heading`}>
          <li>Checking your browser‘s cookie settings</li>
          <li>Disabling content/ad blockers for this site</li>
          <li>Using a private/incognito browser window</li>
        </ul>
        <LinkButton
          variant={"outline"}
          size={"lg"}
          href={retryUrl}
          className="mt-4"
          prefetch={false}
        >
          {retryLabel}
        </LinkButton>
        {process.env.DEBUG_AUTH === "true" && (
          <Card as="section" variant="sub" background="error" className="mt-4">
            <p className="font-semibold">Debug Information:</p>
            <div className="mb-1 text-xs">Error code: {errorCode}</div>
            <pre className="mt-2 max-h-40 overflow-auto rounded bg-slate-100 p-2 font-mono text-xs">
              {JSON.stringify({ diagnosticInfo, debugInfo }, null, 2)}
            </pre>
          </Card>
        )}
      </div>
    </div>
  );
}
